import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import useTheme from "@/hooks/useTheme";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BookOpen, Moon, Sun, LogOut, Menu } from "lucide-react";
import { useState } from "react";

export default function Layout({ nav, title }) {
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data: settings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: async () => (await api.get("/settings")).data,
    enabled: !!user,
  });
  const banner = user?.role === "author" && settings?.banner_enabled && settings?.banner_text ? settings : null;
  const pubName = settings?.publisher_name || "Manda";
  const logo = settings?.login_logo;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-card border-r border-border flex flex-col transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-16 flex items-center gap-2.5 px-6 border-b border-border">
          <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center overflow-hidden">
            {logo ? <img src={logo} alt={pubName} className="h-full w-full object-contain" /> : <BookOpen className="h-5 w-5 text-primary-foreground" strokeWidth={2} />}
          </div>
          <div className="leading-tight min-w-0">
            <p className="font-head font-bold text-[15px] tracking-tight truncate">{pubName}</p>
            <p className="text-[11px] text-muted-foreground -mt-0.5">Publishers</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${item.testid}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <item.icon className="h-[18px] w-[18px]" strokeWidth={2} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9">
              {user?.avatar_url && <AvatarImage src={user.avatar_url} alt={user?.name} />}
              <AvatarFallback className="bg-secondary text-secondary-foreground text-xs font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate capitalize">{user?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive mt-1"
          >
            <LogOut className="h-[18px] w-[18px]" /> Sign out
          </Button>
        </div>
      </aside>

      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {banner && (
          <a
            href={banner.banner_link || undefined}
            target={banner.banner_link ? "_blank" : undefined}
            rel="noopener noreferrer"
            data-testid="author-banner"
            className="w-full flex items-center justify-center text-center text-xs sm:text-sm font-medium px-4 truncate"
            style={{
              background: banner.banner_bg || "#1f4a3a",
              color: banner.banner_text_color || "#ffffff",
              height: "30px",
              cursor: banner.banner_link ? "pointer" : "default",
            }}
          >
            {banner.banner_text}
          </a>
        )}
        <header className="h-16 sticky top-0 z-20 flex items-center justify-between px-4 sm:px-8 border-b border-border bg-background/80 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 -ml-2 text-muted-foreground"
              onClick={() => setOpen(true)}
              data-testid="sidebar-toggle"
            >
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="font-head text-xl sm:text-2xl font-semibold tracking-tight">{title}</h1>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={toggle}
            data-testid="theme-toggle"
            className="rounded-full h-9 w-9"
          >
            {dark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </Button>
        </header>
        <main className="flex-1 p-4 sm:p-8 max-w-[1400px] w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
